import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PublicAdmissionsAPI } from "@/lib/sprint4-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, GraduationCap, CheckCircle2, ArrowRight } from "lucide-react";

export default function PublicApply() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    institution_id: "", academic_year_id: "", target_class_id: "",
    first_name: "", last_name: "", dob: "", gender: "",
    previous_school: "",
    guardian_name: "", guardian_email: "", guardian_phone: "", guardian_relation: "parent",
    address: "", city: "",
  });

  useEffect(() => {
    PublicAdmissionsAPI.config(slug)
      .then((c) => {
        setConfig(c);
        if (c.institutions[0]) setForm((f) => ({ ...f, institution_id: c.institutions[0].id }));
      })
      .catch((e) => setError(e?.detail || "Tenant not accepting applications."))
      .finally(() => setLoading(false));
  }, [slug]);

  const yearsForInst = useMemo(
    () => (config?.academic_years || []).filter((y) => y.institution_id === form.institution_id),
    [config, form.institution_id]
  );
  useEffect(() => {
    if (yearsForInst.length && !yearsForInst.some((y) => y.id === form.academic_year_id)) {
      setForm((f) => ({ ...f, academic_year_id: yearsForInst[0].id, target_class_id: "" }));
    }
  }, [yearsForInst, form.academic_year_id]);

  const classesForYear = useMemo(
    () => (config?.classes || []).filter(
      (c) => c.institution_id === form.institution_id && c.academic_year_id === form.academic_year_id
    ),
    [config, form.institution_id, form.academic_year_id]
  );

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      const out = await PublicAdmissionsAPI.apply({ tenant_slug: slug, ...form });
      setSuccess(out);
    } catch (e) {
      setError(e?.message || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md text-center" data-testid="public-apply-error">
          <h1 className="font-display font-extrabold text-3xl tracking-tight mb-2">Not available</h1>
          <p className="text-sm text-muted-foreground">{error || "This admissions portal is not accepting applications right now."}</p>
        </div>
      </div>
    );
  }

  const branding = config.tenant.branding || {};
  const primary = branding.primary_color || "hsl(222 100% 33%)";

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="public-apply-success">
        <div className="max-w-md w-full border border-border rounded-md bg-card p-8 text-center">
          <div className="h-12 w-12 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: `${primary}10`, color: primary }}>
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Application received</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Thank you, <b>{form.guardian_name}</b>. We&apos;ve received your application for{" "}
            <b>{form.first_name} {form.last_name}</b> to <b>{config.tenant.name}</b>.
          </p>
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground mt-6">Reference</div>
          <code className="text-xs font-mono bg-secondary px-2 py-1.5 rounded border inline-block mt-1" data-testid="success-app-id">
            {success.id.slice(-12).toUpperCase()}
          </code>
          <p className="text-xs text-muted-foreground mt-6">
            Our admissions team will be in touch at <code className="font-mono">{form.guardian_email}</code>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-apply-page">
      {/* Top bar */}
      <div className="border-b border-border" style={{ borderBottomColor: `${primary}30` }}>
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-sm flex items-center justify-center font-display font-extrabold text-white" style={{ background: primary }}>
              {(branding.short_name || config.tenant.name.slice(0, 2)).toUpperCase()}
            </div>
            <div>
              <div className="font-display font-extrabold tracking-tight text-lg leading-none">{config.tenant.name}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-1">
                Admissions {yearsForInst[0]?.name || new Date().getFullYear()}
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate("/login")} className="hidden sm:inline-flex">
            Staff sign-in <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-[0.25em] mb-3">
          Public · /apply/{slug}
        </div>
        <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Apply for admission</h1>
        <p className="text-sm text-muted-foreground mt-3 max-w-2xl">
          Submit one application per child. Required fields are marked with
          <span className="text-destructive"> *</span>. Our admissions team will
          reach out within 3–5 working days.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-8">
          {/* Programme */}
          <Section title="Programme" subtitle="Pick where you'd like to apply.">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Institution" required>
                <Select value={form.institution_id} onValueChange={(v) => setForm({ ...form, institution_id: v, academic_year_id: "", target_class_id: "" })}>
                  <SelectTrigger data-testid="apply-institution"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {config.institutions.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Academic year" required>
                <Select value={form.academic_year_id} onValueChange={(v) => setForm({ ...form, academic_year_id: v, target_class_id: "" })}>
                  <SelectTrigger data-testid="apply-year"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {yearsForInst.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Class" required>
                <Select value={form.target_class_id} onValueChange={(v) => setForm({ ...form, target_class_id: v })} disabled={!form.academic_year_id}>
                  <SelectTrigger data-testid="apply-class"><SelectValue placeholder="Select a class" /></SelectTrigger>
                  <SelectContent>
                    {classesForYear.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          {/* Applicant */}
          <Section title="The applicant" subtitle="Details about the child.">
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name" required><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="apply-first-name" /></Field>
              <Field label="Last name" required><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="apply-last-name" /></Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Date of birth"><Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="apply-dob" /></Field>
              <Field label="Gender">
                <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                  <SelectTrigger data-testid="apply-gender"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Previous school"><Input value={form.previous_school} onChange={(e) => setForm({ ...form, previous_school: e.target.value })} data-testid="apply-prev-school" /></Field>
            </div>
          </Section>

          {/* Guardian */}
          <Section title="Parent / Guardian" subtitle="Who should we keep in touch with?">
            <Field label="Full name" required><Input value={form.guardian_name} onChange={(e) => setForm({ ...form, guardian_name: e.target.value })} required data-testid="apply-guardian-name" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email" required><Input type="email" value={form.guardian_email} onChange={(e) => setForm({ ...form, guardian_email: e.target.value })} required data-testid="apply-guardian-email" /></Field>
              <Field label="Phone"><Input value={form.guardian_phone} onChange={(e) => setForm({ ...form, guardian_phone: e.target.value })} data-testid="apply-guardian-phone" /></Field>
            </div>
            <Field label="Relation">
              <Select value={form.guardian_relation} onValueChange={(v) => setForm({ ...form, guardian_relation: v })}>
                <SelectTrigger data-testid="apply-guardian-relation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="father">Father</SelectItem>
                  <SelectItem value="mother">Mother</SelectItem>
                  <SelectItem value="guardian">Guardian</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-3">
              <Field label="Address"><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} /></Field>
              <Field label="City"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            </div>
          </Section>

          {error && (
            <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2" data-testid="apply-error">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between gap-4 pt-2">
            <p className="text-xs text-muted-foreground max-w-md">
              By submitting, you consent to {config.tenant.name} contacting you
              about this application.
            </p>
            <Button
              type="submit"
              disabled={submitting || !form.target_class_id}
              size="lg"
              className="font-semibold"
              style={{ background: primary }}
              data-testid="apply-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <GraduationCap className="h-4 w-4 mr-2" />}
              Submit application
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <div className="mb-4">
        <h3 className="font-display font-extrabold text-lg tracking-tight">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wider">
        {label}{required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
